import React from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import {
  defaultSliderConfig,
  type SliderConfig,
} from "../../data/SliderConfig";
import type { FeatureItem } from "../../types/feature";
import { useCategoriesWithProducts } from "../../hooks/useSupabase";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

interface SupabaseFeatureOneProps {
  sectionId?: string;
  containerClass?: string;
  sliderConfig?: Partial<SliderConfig>;
  categoryId?: number;
  includeProductCount?: boolean;
  limit?: number;
  offset?: number;
  locale?: string;
}

const SupabaseFeatureOne: React.FC<SupabaseFeatureOneProps> = ({
  sectionId = "featureSection",
  containerClass = "container mx-auto",
  sliderConfig = {},
  categoryId,
  includeProductCount = false,
  limit = 20,
  offset = 0,
  locale = "en",
}) => {
  void locale;
  const navigate = useNavigate();
  const { categoriesWithSubs, loading, error } = useCategoriesWithProducts();
  const navigateToCategory = (categoryId: number) => {
    const params = new URLSearchParams({
      category_id: categoryId.toString(),
      page: "1",
      limit: "20",
    });
    navigate(`/products?${params.toString()}`);
  };

  const features: FeatureItem[] = React.useMemo(() => {
    let filteredCategories = categoriesWithSubs;

    if (categoryId) {
      filteredCategories = categoriesWithSubs.filter(
        (category) => category.id === categoryId,
      );
    }

    if (limit && limit > 0) {
      filteredCategories = filteredCategories.slice(offset, offset + limit);
    }

    return filteredCategories.map((category: any) => {
      const iconPath =
        category.icon_path || category.sub_categories?.[0]?.icon_path || "";
      const imageUrl = `https://gzvhulvbgyymgjgdalpa.supabase.co/storage/v1/object/public/publicimages/category_images/${iconPath}`;

      return {
        id: category.id.toString(),
        name: category.name,
        image: imageUrl,
        productCount: category.sub_categories?.length || 0,
        link: "",
      };
    });
  }, [categoriesWithSubs, categoryId, limit, offset]);

  const refetch = () => {
    window.location.reload();
  };

  const getCategoryKey = (categoryName: string) => {
    return categoryName.toLowerCase().replace(/[^a-zA-Z0-9]/g, "-");
  };

  const renderFeatureItem = (item: FeatureItem) => (
    <div
      key={item.id}
      data-category={getCategoryKey(item.name)}
      className="mt-5 px-2 animate-fade-in"
    >
      <div
        onClick={() => navigateToCategory(parseInt(item.id))}
        className="group rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-neutral-800 hover:border-primary relative overflow-hidden min-h-55 flex flex-col justify-end bg-center bg-cover"
        style={{ backgroundImage: `url('${item.image}')` }}
      >
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-linear-to-br from-primary-100 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>

        {/* Bottom rectangle for text */}
        <div
          className="absolute left-0 bottom-0 w-full"
          style={{ height: "20%" }}
        >
          <div className="w-full h-full bg-primary/90 flex flex-col items-center justify-center px-2">
            <h6 className="text-base font-semibold text-white text-center truncate w-full">
              {item.name}
            </h6>
            {includeProductCount && (
              <p className="text-sm text-primary-100 text-center w-full truncate">
                {item.productCount} items
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderLoadingSkeleton = () => (
    <div className="mt-5 px-2">
      <div className="bg-neutral-900 rounded-2xl p-6 shadow-sm border border-neutral-800">
        <div className="w-24 h-24 mx-auto mb-4 bg-neutral-800 animate-pulse rounded-lg" />
        <div className="text-center">
          <div className="h-5 bg-neutral-800 rounded mb-2 mx-auto w-28 animate-pulse"></div>
          {includeProductCount && (
            <div className="h-4 bg-neutral-800 rounded mx-auto w-16 animate-pulse"></div>
          )}
        </div>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="text-center py-8">
      <div className="text-primary-600 mb-4">
        <i className="ph ph-warning-circle text-4xl"></i>
      </div>
      <h6 className="text-lg mb-2">Failed to load features</h6>
      <p className="text-neutral-500 mb-4">{error}</p>
      <button
        onClick={refetch}
        className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm"
      >
        Try Again
      </button>
    </div>
  );

  function SampleNextArrow(props: any) {
    const { className, onClick } = props;
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${className} slick-next slick-arrow flex items-center justify-center rounded-full text-xl bg-neutral-900 shadow-lg border border-neutral-800 hover:bg-primary hover:border-primary transition-transform duration-200 outline-none`}
        style={{ color: "#4b5563" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "white")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#4b5563")}
        disabled={loading}
      >
        <i className="ph ph-caret-right" />
      </button>
    );
  }

  function SamplePrevArrow(props: any) {
    const { className, onClick } = props;
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${className} slick-prev slick-arrow flex items-center justify-center rounded-full text-xl bg-neutral-900 shadow-lg border border-neutral-800 hover:bg-primary hover:border-primary transition-transform duration-200 outline-none`}
        style={{ color: "#4b5563" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "white")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#4b5563")}
        disabled={loading}
      >
        <i className="ph ph-caret-left" />
      </button>
    );
  }

  const settings = {
    ...defaultSliderConfig,
    ...sliderConfig,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: sliderConfig.responsive || defaultSliderConfig.responsive,
  };

  return (
    <div className="py-14 md:py-16 px-4 overflow-x-hidden" id={sectionId}>
      <div className={containerClass}>
        {/* Section Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Featured Categories
            </h2>
            <p className="text-neutral-400">
              Choose your necessary products from this feature categories.
            </p>
          </div>

          {/* Custom navigation arrows for desktop */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => {
                const slider = document.querySelector(".slick-slider");
                if (slider) {
                  const prevButton = slider.querySelector(
                    ".slick-prev",
                  ) as HTMLButtonElement;
                  prevButton?.click();
                }
              }}
              className="w-10 h-10 rounded-full border-2 border-neutral-700 text-white flex items-center justify-center hover:border-primary hover:text-primary transition-all"
            >
              <i className="ph ph-caret-left text-lg"></i>
            </button>
            <button
              onClick={() => {
                const slider = document.querySelector(".slick-slider");
                if (slider) {
                  const nextButton = slider.querySelector(
                    ".slick-next",
                  ) as HTMLButtonElement;
                  nextButton?.click();
                }
              }}
              className="w-10 h-10 rounded-full border-2 border-neutral-700 text-white flex items-center justify-center hover:border-primary hover:text-primary transition-all"
            >
              <i className="ph ph-caret-right text-lg"></i>
            </button>
          </div>
        </div>

        <div className="relative border-t border-neutral-800">
          <div className="-mx-2">
            {error ? (
              renderError()
            ) : (
              <Slider {...settings}>
                {loading
                  ? [...Array(6)].map((_, index) => (
                      <div key={`skeleton-${index}`}>
                        {renderLoadingSkeleton()}
                      </div>
                    ))
                  : features.length > 0
                    ? features.map(renderFeatureItem)
                    : [...Array(3)].map((_, index) => (
                        <div
                          key={`empty-${index}`}
                          className="mt-5 text-center px-2"
                        >
                          <div className="bg-white rounded-2xl p-8 border-2 border-dashed border-gray-200">
                            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                              <i className="ph ph-image text-4xl text-gray-400"></i>
                            </div>
                            <h6 className="text-gray-400">No categories yet</h6>
                          </div>
                        </div>
                      ))}
              </Slider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupabaseFeatureOne;
